import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Shield, Target, Zap, Heart, TrendingUp, Award } from 'lucide-react';
import { toast } from 'sonner';

function TruthCourageBuilder({ onBack }) {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [couragePoints, setCouragePoints] = useState(0);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [userResponse, setUserResponse] = useState('');

  const couragelevels = [
    {
      level: 1,
      title: "Truth Explorer",
      description: "Recognize what you're withholding",
      challenges: [
        {
          title: "What am I not saying?",
          prompt: "What truth are you withholding from someone close to you?",
          example: "I haven't told my partner I'm unhappy with how much they work",
          followUp: "What are you afraid will happen if you tell the truth?"
        },
        {
          title: "Protection Pattern",
          prompt: "Who are you 'protecting' by not sharing your truth?",
          example: "I'm protecting my mom by not telling her I disagree with her politics",
          followUp: "What feeling in yourself are you actually protecting against?"
        }
      ]
    },
    {
      level: 2,
      title: "Fear Investigator", 
      description: "Dig into the layers of protection",
      challenges: [
        {
          title: "Layer One: Protecting Them",
          prompt: "What do you tell yourself about why you can't share your truth?",
          example: "I don't want to hurt their feelings / I don't want to rock the boat",
          followUp: "What would you have to feel if you saw them upset?"
        },
        {
          title: "Layer Two: Protecting Yourself",
          prompt: "What feeling are you really trying to avoid in yourself?",
          example: "If I tell the truth and they get upset, I'll have to feel guilt, fear of abandonment, or rejection",
          followUp: "What's the worst that would really happen if you felt that feeling?"
        }
      ]
    },
    {
      level: 3,
      title: "Courage Practitioner",
      description: "Take calculated risks with truth-telling",
      challenges: [
        {
          title: "Small Risk Practice",
          prompt: "Choose a low-stakes situation to practice authentic expression",
          example: "Tell a friend you'd prefer tea instead of coffee when they offer",
          followUp: "How did it feel to express a preference?"
        },
        {
          title: "Medium Risk Practice",
          prompt: "Share a vulnerable feeling with someone you trust",
          example: "Tell a friend 'I felt hurt when you interrupted me earlier'",
          followUp: "What did you learn about their capacity to hear you?"
        }
      ]
    },
    {
      level: 4,
      title: "Truth Warrior",
      description: "Transform fear into authentic power",
      challenges: [
        {
          title: "Dreaded Experience Navigator",
          prompt: "Risk expressing something you've been afraid to say",
          example: "Have the conversation you've been avoiding for months",
          followUp: "How did the reality compare to your fears?"
        },
        {
          title: "Celebration Practice",
          prompt: "Take in the good when authentic expression goes well",
          example: "Acknowledge your courage and the positive results",
          followUp: "How can you use this success to build more courage?"
        }
      ]
    }
  ];

  const dreadedExperiences = [
    "Fear of rejection",
    "Fear of judgment", 
    "Fear of abandonment",
    "Fear of conflict",
    "Fear of disappointing others",
    "Fear of being seen as selfish",
    "Fear of not being loved",
    "Fear of being wrong",
    "Fear of vulnerability",
    "Fear of losing control"
  ];

  const startChallenge = (challenge) => {
    setCurrentChallenge(challenge);
    setUserResponse('');
    
    // Play challenge start sound
    const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA1NTU1NTU1NTU1NTU1NTU1NTU1');
    audio.volume = 0.4;
    audio.play().catch(() => {});
    
    toast.success(`Challenge started: ${challenge.title}`);
  };

  const completeChallenge = () => {
    if (userResponse.trim()) {
      const completed = {
        id: Date.now(),
        level: currentLevel,
        challenge: currentChallenge.title,
        response: userResponse,
        timestamp: new Date().toLocaleString()
      };
      
      setCompletedChallenges(prev => [...prev, completed]);
      setCouragePoints(prev => prev + currentLevel * 10);
      
      // Level up check
      const levelChallenges = couragelevels[currentLevel - 1].challenges.length;
      const completedAtLevel = completedChallenges.filter(c => c.level === currentLevel).length + 1;
      
      if (completedAtLevel >= levelChallenges && currentLevel < couragelevels.length) {
        setCurrentLevel(prev => prev + 1);
        toast.success(`🎉 Level Up! You're now a ${couragelevels[currentLevel].title}!`);
      } else {
        toast.success(`+${currentLevel * 10} courage points! You're building authentic power.`);
      }
      
      setCurrentChallenge(null);
      setUserResponse('');
      
      // Play completion sound
      const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA4ODg4ODg4ODg4ODg4ODg4ODg4');
      audio.volume = 0.6;
      audio.play().catch(() => {});
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_belonging-sessions/artifacts/femlgvnm_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="min-h-screen bg-orange-900 bg-opacity-85 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
              ← Back to Games & Tools
            </Button>
            <h1 className="text-4xl font-bold text-white mb-4">💪 Truth Courage Builder</h1>
            <p className="text-xl text-orange-200">Transform fear of feeling into authentic expression</p>
            
            <div className="flex justify-center gap-6 mt-6">
              <Badge className="bg-orange-500 text-white text-lg px-4 py-2">
                🏆 Level {currentLevel}: {couragelevels[currentLevel - 1].title}
              </Badge>
              <Badge className="bg-yellow-500 text-black text-lg px-4 py-2">
                ⚡ {couragePoints} Courage Points
              </Badge>
            </div>
          </div>

          {!currentChallenge ? (
            /* Level Selection */
            <div className="space-y-8">
              <Card className="border-4 border-orange-500 bg-white" style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-2xl text-orange-800 text-center">
                    Choose Your Truth Courage Challenge
                  </CardTitle>
                  <p className="text-center text-orange-700">
                    {couragelevels[currentLevel - 1].description}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {couragelevels[currentLevel - 1].challenges.map((challenge, i) => (
                      <Card key={i} className="border-3 border-orange-400 bg-orange-50 cursor-pointer hover:scale-105 transition-all" style={{ borderRadius: 0 }} onClick={() => startChallenge(challenge)}>
                        <CardHeader>
                          <CardTitle className="text-lg text-orange-800">{challenge.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-orange-700 mb-3">{challenge.prompt}</p>
                          <div className="bg-white p-3 border-l-4 border-orange-400">
                            <p className="text-xs text-gray-600 font-bold mb-1">Example:</p>
                            <p className="text-xs text-gray-700 italic">{challenge.example}</p>
                          </div>
                          <Button 
                            className="w-full mt-3 bg-orange-600 text-white hover:bg-orange-700"
                            style={{ borderRadius: 0 }}
                          >
                            <Target className="w-4 h-4 mr-2" />
                            Take This Challenge
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Dreaded Experiences Awareness */}
              <Card className="border-4 border-red-500 bg-white" style={{ borderRadius: 0 }}>
                <CardHeader>
                  <CardTitle className="text-xl text-red-800">😱 Common Dreaded Experiences</CardTitle>
                  <p className="text-sm text-red-700">Click any you relate to - awareness is the first step</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {dreadedExperiences.map((fear) => (
                      <Button
                        key={fear}
                        onClick={() => {
                          toast.info(`Remember: This fear was formed to protect you, but you're stronger now.`);
                          const audio = new Audio('data:audio/wav;base64,UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAyMjIyMjIyMjIyMjIyMjIyMjIy');
                          audio.volume = 0.3;
                          audio.play().catch(() => {});
                        }}
                        variant="outline"
                        className="border-red-400 text-red-600 hover:bg-red-50 text-sm py-2"
                        style={{ borderRadius: 0 }}
                      >
                        {fear}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Active Challenge */
            <Card className="border-4 border-orange-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  {currentChallenge.title}
                </CardTitle>
                <p className="text-orange-700">Level {currentLevel} Challenge</p>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div className="bg-orange-50 p-6 border-l-4 border-orange-500">
                  <p className="text-lg text-orange-800 mb-4">{currentChallenge.prompt}</p>
                  <div className="bg-white p-4 border border-orange-300">
                    <p className="text-sm text-gray-600 font-bold mb-1">Example:</p>
                    <p className="text-sm text-gray-700 italic">{currentChallenge.example}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">Your Response:</label>
                  <Textarea
                    placeholder="Write about your situation, your fears, your truth..."
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    className="border-orange-500 min-h-[150px]"
                    style={{ borderRadius: 0 }}
                  />
                </div>

                <div className="bg-blue-50 p-4 border-l-4 border-blue-500">
                  <p className="text-sm text-blue-800 font-bold mb-2">Follow-up Question:</p>
                  <p className="text-sm text-blue-700 italic">{currentChallenge.followUp}</p>
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={() => setCurrentChallenge(null)}
                    variant="outline"
                    className="border-orange-500 text-orange-600"
                    style={{ borderRadius: 0 }}
                  >
                    ← Back to Challenges
                  </Button>
                  <Button 
                    onClick={completeChallenge}
                    className="bg-orange-600 text-white hover:bg-orange-700 flex-1"
                    style={{ borderRadius: 0 }}
                    disabled={!userResponse.trim()}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Complete Challenge
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Courage Journey */}
          {completedChallenges.length > 0 && (
            <Card className="mt-8 border-4 border-green-500 bg-white" style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="text-xl text-green-800 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Your Courage Journey
                </CardTitle>
                <p className="text-sm text-green-700">Celebrating your authentic expression wins</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {completedChallenges.slice(-10).reverse().map((challenge) => (
                    <div key={challenge.id} className="p-4 bg-green-50 border-l-4 border-green-500">
                      <div className="flex justify-between items-start mb-2">
                        <Badge className="bg-green-500 text-white text-xs">Level {challenge.level}</Badge>
                        <span className="text-xs text-gray-500">{challenge.timestamp}</span>
                      </div>
                      <p className="text-sm font-bold text-gray-800">{challenge.challenge}</p>
                      <p className="text-xs text-gray-600 mt-1">{challenge.response.substring(0, 100)}...</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Wisdom Reminder */}
          <Card className="mt-8 border-4 border-yellow-500 bg-white" style={{ borderRadius: 0 }}>
            <CardContent className="p-6 text-center">
              <Heart className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <p className="text-lg italic text-gray-800 mb-4">
                "We are basically scared to feel whatever we'll have to feel when we see them react to our truth. 
                But when we withhold our truth, we rob the other person of the opportunity to grow."
              </p>
              <div className="bg-yellow-50 p-4 border-l-4 border-yellow-500 mt-4">
                <p className="text-base text-yellow-800 font-bold">
                  🌟 Remember: Your authentic expression is a gift to the world.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

export default TruthCourageBuilder;